import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useMedia } from 'react-use';
import { type Customer } from '@hungryroot/customer';
import { Switch } from '@hungryroot/pho';
import { type PatchedCustomerTagList, type Tag } from '@hungryroot/rest';
import { usePatchCustomerTags, useTagGroup } from '@hungryroot/rest-hooks';
import {
  getCustomerTagsQueryKey,
  tagGroups,
  tags,
  type TagGroups,
  useCustomerTags,
  useTag,
  getTagDetails,
  useCustomerTagGroup,
} from '@hungryroot/tags';
import { ArrowRightTurn, BreakpointQueryLarge } from '@hungryroot/ui-kit';
import styles from './ControlTagSwitch.module.css';

export enum TagPreference {
  Selected = 1,
  NotSelected = 0,
}

export enum DislikeTagPreference {
  Selected = -1,
  NotSelected = 0,
}

export enum Variant {
  Like = 'like',
  Dislike = 'dislike',
}

const getSwitchIcon = (tag: Tag) => {
  if (!tag) return null;
  if (tag.id === tags.OnlyLowSodium) {
    return (
      <span className={styles['only-low-sodium-icon']}>
        <ArrowRightTurn />
      </span>
    );
  }

  const imageUrl = tag.image_url || tag.thumbnail_url;
  if (!imageUrl) return null;
  return <img alt={tag.name} src={imageUrl} className={styles.icon} />;
};

interface BuildPayloadArgs {
  tag: Tag;
  customerId: Customer['id'];
  selected: boolean;
  customerTags: PatchedCustomerTagList[];
}

function buildDislikesData({
  tag,
  customerId,
  selected,
  customerTags,
}: BuildPayloadArgs) {
  const payload: PatchedCustomerTagList[] = [
    {
      customer_id: customerId,
      tag: tag.id,
      preference: selected
        ? DislikeTagPreference.Selected
        : DislikeTagPreference.NotSelected,
    },
  ];

  const count = customerTags.filter(
    (obj) => obj.preference === DislikeTagPreference.Selected
  ).length;

  return { payload, count };
}

function buildData({
  tag,
  customerId,
  selected,
  customerTags,
  variant,
}: BuildPayloadArgs & { variant?: Variant }) {
  if (variant === Variant.Dislike)
    return buildDislikesData({
      tag,
      customerId,
      selected,
      customerTags,
    });

  const buildPayload = (): PatchedCustomerTagList[] => {
    const tagPayload = {
      customer_id: customerId,
      tag: tag.id,
      preference: selected ? TagPreference.Selected : TagPreference.NotSelected,
    };

    if (tag.id === tags.LowSodium && !selected) {
      return [
        // If Low Sodium is deselected, Only Low Sodium should also be deselected
        {
          customer_id: customerId,
          tag: tags.OnlyLowSodium,
          preference: TagPreference.NotSelected,
        },
        tagPayload,
      ];
    }

    return [tagPayload];
  };

  const count = customerTags.filter(
    (obj) => obj.preference === TagPreference.Selected
  ).length;

  return { payload: buildPayload(), count };
}

interface BuildSelectedMapArgs {
  customerTags: PatchedCustomerTagList[];
  queryTags?: Tag[];
}

const buildDislikesSelectedMap = ({
  customerTags,
  queryTags,
}: BuildSelectedMapArgs) => {
  const selectedMap: { [key: number]: boolean } | undefined = queryTags?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: customerTags.some(
        (obj) =>
          obj.tag === cur.id && obj.preference === DislikeTagPreference.Selected
      ),
    }),
    {} as { [key: number]: boolean }
  );

  return selectedMap;
};

const buildSelectedMap = ({
  customerTags,
  queryTags,
  variant,
}: BuildSelectedMapArgs & { variant?: Variant }):
  | {
      [key: number]: boolean;
    }
  | undefined => {
  if (variant === Variant.Dislike) {
    return buildDislikesSelectedMap({ customerTags, queryTags });
  }

  const selectedMap = queryTags?.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: customerTags.some(
        (obj) => obj.tag === cur.id && obj.preference === TagPreference.Selected
      ),
    }),
    {} as { [key: number]: boolean }
  );

  return selectedMap;
};

function useTagGroupData(tagGroupId: TagGroups) {
  const tagGroup = useTagGroup(tagGroupId);
  const lowSodiumHardRestraint = useTag(tags.OnlyLowSodium)?.data;
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;

  if (
    tagGroupId === tagGroups.nutrition &&
    lowSodiumHardRestraint &&
    queryTags
  ) {
    return [...queryTags, lowSodiumHardRestraint];
  }

  return queryTags;
}

function sortNutritionTags(
  tagsToSort: Tag[] | undefined,
  tagGroupId: TagGroups,
  isDesktop: boolean
) {
  if (
    tagGroupId !== tagGroups.nutrition ||
    !tagsToSort ||
    !tagsToSort.find((tag) => tag.id === tags.OnlyLowSodium)
  )
    return tagsToSort;

  const sortedTags = tagsToSort.filter(
    (tag) => tag.id !== tags.LowSodium && tag.id !== tags.OnlyLowSodium
  );
  const lowSodiumTags = tagsToSort.filter(
    (tag) => tag.id === tags.LowSodium || tag.id === tags.OnlyLowSodium
  );

  const lowSodiumIndex = isDesktop ? sortedTags.length - 1 : sortedTags.length;

  sortedTags.splice(lowSodiumIndex, 0, lowSodiumTags[0]);
  sortedTags.push(lowSodiumTags[1]);
  return sortedTags;
}

interface Props {
  testid?: string;
  customerId: Customer['id'];
  tagGroupId: TagGroups;
  variant?: Variant;
  warning?: {
    threshold: number;
    onThresholdReached: (params: { onConfirm: () => void }) => void;
  };
  colCount: 1 | 2;
}

export function ControlTagSwitch(props: Props) {
  const { testid, customerId, tagGroupId, warning, colCount, variant } = props;
  const [customerTags, setCustomerTags] = useState<PatchedCustomerTagList[]>(
    []
  );

  const isDesktop = useMedia(BreakpointQueryLarge);

  const patchCustomerTagsMutation = usePatchCustomerTags({
    invalidationKeys: [
      { queryKey: getCustomerTagsQueryKey([tags.OnlyLowSodium]) },
    ],
  });

  const queryTags = useTagGroupData(tagGroupId);

  const { data: customerTagGroupData, status: customerTagGroupStatus } =
    useCustomerTagGroup(tagGroupId);

  const {
    data: sodiumHardRestraintTagData,
    status: sodiumHardRestraintTagStatus,
  } = useCustomerTags([tags.OnlyLowSodium]);

  useEffect(() => {
    if (
      customerTagGroupStatus !== 'success' ||
      customerTagGroupData === undefined ||
      (tagGroups.nutrition === tagGroupId &&
        sodiumHardRestraintTagStatus !== 'success')
    ) {
      return;
    }

    const prefs = customerTagGroupData.results.map((obj) => {
      return {
        customer_id: customerId,
        preference: obj.preference,
        tag: obj.tag,
      };
    });

    if (tagGroups.nutrition === tagGroupId) {
      const hardConstraintTag = sodiumHardRestraintTagData?.results?.[0];

      const hardConstraintTagData = hardConstraintTag
        ? [
            {
              customer_id: customerId,
              preference: hardConstraintTag.preference,
              tag: hardConstraintTag.tag,
            },
          ]
        : [];

      setCustomerTags([...prefs, ...hardConstraintTagData]);

      return;
    }

    setCustomerTags(prefs);
  }, [
    customerTagGroupData,
    customerTagGroupStatus,
    sodiumHardRestraintTagData,
    sodiumHardRestraintTagStatus,
  ]);

  function handleChange(tag: Tag, selected: boolean) {
    const { payload, count } = buildData({
      tag,
      customerId,
      selected,
      customerTags,
      variant,
    });
    const { threshold, onThresholdReached } = warning || {};
    const isThreshold = threshold && count >= threshold;

    if (isThreshold && selected && onThresholdReached) {
      return onThresholdReached({
        onConfirm: () => {
          patchCustomerTagsMutation.mutate({ data: payload });
        },
      });
    }
    return patchCustomerTagsMutation.mutate({ data: payload });
  }

  const selectedMap = buildSelectedMap({ customerTags, queryTags, variant });

  const switchGroup = sortNutritionTags(queryTags, tagGroupId, isDesktop)
    ?.filter(
      (tag) =>
        Boolean(tag) &&
        (tag.id !== tags.OnlyLowSodium ||
          Boolean(selectedMap?.[tags.LowSodium]))
    )
    .map((tag) => {
      // Exclude Low Calorie and No Added Sugar, both part of Nutrition Group but not rendered in UI
      if (tag.id === tags.LowCalorie || tag.id === tags.NoSugar) {
        return;
      }

      const details = getTagDetails(tag);
      const { icon, name } = details;
      const key = tag.id as keyof typeof selectedMap;
      const isEnabled = selectedMap ? selectedMap[key] : false;

      if (
        (tagGroupId === tagGroups.tools ||
          tagGroupId === tagGroups.nutrition) &&
        icon === undefined &&
        tag.id !== tags.OnlyLowSodium
      ) {
        return;
      }

      return (
        <Switch
          key={tag.id}
          testid={`${testid}-${tag.id}`}
          label={name || tag.name}
          subtext={tag.remark}
          enabled={isEnabled}
          onChange={() => {
            handleChange(tag, !isEnabled);
          }}
          icon={getSwitchIcon(tag)}
        />
      );
    });

  return (
    <div
      data-testid={testid}
      className={clsx({
        [styles['col-1']]: colCount === 1,
        [styles['col-2']]: colCount === 2,
      })}
    >
      {switchGroup}
    </div>
  );
}
